"use client";

// ── The graded-solution draft (`EX-PUB01`, shared from `EX-PUB08`) ──────────
// Lifted out of `ProblemWorkspaceClient` unchanged the day `/judge/[slug]`
// gained a second caller. A contest problem is the same editor pointed at a
// different route, and "seed from the first graded runtime, then persist what
// the user typed" copied twice is two places for the two surfaces to drift -
// including the one that matters mid-contest, where losing a draft to a reload
// costs a contestant the round.

import { useCallback, useEffect, useRef, useState } from "react";

import { getRuntimeDefaultSourceCode } from "@/features/quiz/lib/coding-task-format";

export type SolutionDraft = {
  languageKey: string;
  languageVersion: string;
  sourceCode: string;
};

/** One `(language, version)` the graded runner will accept. */
export type AllowedRuntime = { languageKey: string; languageVersion: string };

function readStoredDraft(storageKey: string): SolutionDraft | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SolutionDraft>;
    if (
      typeof parsed.languageKey === "string" &&
      typeof parsed.languageVersion === "string" &&
      typeof parsed.sourceCode === "string"
    ) {
      return parsed as SolutionDraft;
    }
  } catch {
    // A corrupt draft is just no draft.
  }
  return null;
}

function storeDraft(storageKey: string, draft: SolutionDraft): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(draft));
  } catch {
    // Quota or private mode - losing the convenience is fine.
  }
}

/** `EX-FNC05`: each language's own text on a function problem, beside the draft itself. */
function sourcesKey(storageKey: string): string {
  return `${storageKey}:sources`;
}

function readStoredSources(storageKey: string): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(sourcesKey(storageKey)) ?? "{}");
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, string] => typeof entry[1] === "string"),
    );
  } catch {
    return {};
  }
}

function storeSources(storageKey: string, sources: Record<string, string>): void {
  try {
    window.localStorage.setItem(sourcesKey(storageKey), JSON.stringify(sources));
  } catch {
    // As above.
  }
}

/**
 * The runtime a fresh draft opens on: the preferred one when the runner offers
 * it, the same language at another version when only the version is missing,
 * else the catalogue's first.
 */
export function seedRuntime(
  allowedRuntimes: AllowedRuntime[],
  preferred: AllowedRuntime | null,
): AllowedRuntime | undefined {
  if (preferred) {
    const exact = allowedRuntimes.find(
      (runtime) =>
        runtime.languageKey === preferred.languageKey &&
        runtime.languageVersion === preferred.languageVersion,
    );
    if (exact) return exact;
    const sameLanguage = allowedRuntimes.find((runtime) => runtime.languageKey === preferred.languageKey);
    if (sameLanguage) return sameLanguage;
  }
  return allowedRuntimes[0];
}

/**
 * The editor's draft for one problem, persisted under `storageKey`.
 *
 * First mount: a stored draft wins; otherwise {@link seedRuntime} and its
 * starter code. Never overwrites what the user has already typed.
 *
 * `preferred` (`EX-PUB21`) is read when the draft is seeded, so a caller can
 * pass a reader of browser storage without reading it during a server render.
 * Absent, the first graded runtime is used, as before.
 *
 * `allowedRuntimes` must be referentially stable (memoise it at the call site)
 * - it is an effect dependency, and a fresh array every render would re-seed.
 *
 * `EX-FNC05`, a function problem (`starters`, one per language the problem
 * offers): a fresh draft opens on its language's starter, a stored draft in a
 * language the problem does not offer is set aside rather than shown, and each
 * language keeps its own text, so `sourceFor` hands a language switch back
 * what was written in that language, else its starter. `ready` holds the seed
 * until the caller knows whether the problem is one (its view has loaded);
 * absent, the hook seeds as before.
 */
export function useSolutionDraft(
  storageKey: string,
  allowedRuntimes: AllowedRuntime[],
  options: {
    preferred?: () => AllowedRuntime | null;
    starters?: Readonly<Record<string, string>> | null;
    ready?: boolean;
  } = {},
) {
  const [draft, setDraft] = useState<SolutionDraft | null>(null);
  const draftRef = useRef<SolutionDraft | null>(null);
  draftRef.current = draft;
  const ready = options.ready ?? true;
  const starters = options.starters ?? null;
  const startersRef = useRef(starters);
  startersRef.current = starters;
  /** The text each language last held, read once per key. */
  const sources = useRef<{ key: string; byLanguage: Record<string, string> } | null>(null);
  const sourcesFor = useCallback(() => {
    if (sources.current?.key !== storageKey) {
      sources.current = { key: storageKey, byLanguage: readStoredSources(storageKey) };
    }
    return sources.current.byLanguage;
  }, [storageKey]);

  useEffect(() => {
    if (draft || !ready) return;
    const stored = readStoredDraft(storageKey);
    if (stored && (!starters || stored.languageKey in starters)) {
      setDraft(stored);
      return;
    }
    const seed = seedRuntime(allowedRuntimes, options.preferred?.() ?? null);
    if (seed) {
      setDraft({
        languageKey: seed.languageKey,
        languageVersion: seed.languageVersion,
        sourceCode: starters
          ? (sourcesFor()[seed.languageKey] ?? starters[seed.languageKey] ?? "")
          : getRuntimeDefaultSourceCode(seed.languageKey),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedRuntimes, storageKey, ready]);

  const persistTimer = useRef<number | null>(null);
  const pushDraft = useCallback(
    (next: SolutionDraft) => {
      const previous = draftRef.current;
      draftRef.current = next;
      setDraft(next);
      if (startersRef.current) {
        // The language left keeps its text, even one never edited since it loaded.
        if (previous && previous.languageKey !== next.languageKey) sourcesFor()[previous.languageKey] = previous.sourceCode;
        sourcesFor()[next.languageKey] = next.sourceCode;
      }
      if (persistTimer.current) window.clearTimeout(persistTimer.current);
      persistTimer.current = window.setTimeout(() => {
        storeDraft(storageKey, next);
        if (startersRef.current) storeSources(storageKey, sourcesFor());
      }, 400);
    },
    [storageKey, sourcesFor],
  );

  /** `EX-FNC05`: what a switch to `languageKey` opens on: its own text, else its starter. */
  const sourceFor = useCallback(
    (languageKey: string): string =>
      sourcesFor()[languageKey] ?? startersRef.current?.[languageKey] ?? getRuntimeDefaultSourceCode(languageKey),
    [sourcesFor],
  );

  return { draft, pushDraft, sourceFor };
}
